import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { EMPTY, finalize, switchMap } from 'rxjs';
import { LoadingService } from '../../services/general/loading.service';
import { InventoryAction, InventoryAssignPayload, InventoryMetricsFilters, InventoryQueryFilters, InventoryUpdatePayload, InventarioService } from '../../services/inventario/inventario.service';

type ActionMeta = {
  label: string;
  title: string;
  description: string;
  needsFile: boolean;
};

type InventoryResponseAlert = {
  tipo: string;
  mensaje: string;
  placa_inventario?: string;
};

type InventoryRevisionAlert = {
  placa_inventario: string;
  mensaje: string;
};

@Component({
  selector: 'app-inventario',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './inventario.component.html',
  styleUrl: './inventario.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class InventarioComponent {
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  readonly actionOptions: Array<{ value: InventoryAction; label: string }> = [
    { value: 'metrics', label: 'Cargar archivo' },
    { value: 'query', label: 'Consulta' },
    { value: 'update', label: 'Actualizar' },
    { value: 'return-to-stock', label: 'Regresar a Stock' },
    { value: 'assign', label: 'Asignar' },
    { value: 'return', label: 'Devolver a proveedor' }
  ];

  readonly actionMeta: Record<InventoryAction, ActionMeta> = {
    metrics: {
      label: 'Cargar archivo',
      title: 'Inventario — Mesa de Servicio',
      description: 'Carga tu archivo CSV para procesar el inventario, validar datos y preparar la información para registro en base de datos.',
      needsFile: true
    },
    load: {
      label: 'Load',
      title: 'Carga confirmada de inventario',
      description: 'Registra el CSV en DynamoDB.',
      needsFile: true
    },
    query: {
      label: 'Consulta',
      title: 'Consulta el inventario',
      description: 'Filtra por estado, placa o usuario.',
      needsFile: false
    },
    update: {
      label: 'Actualizar',
      title: 'Actualiza campos del inventario',
      description: 'Busca una placa y modifica solo los campos permitidos.',
      needsFile: false
    },
    'return-to-stock': {
      label: 'Regresar a Stock',
      title: 'Devuelve un equipo a stock',
      description: 'Devuelve la placa seleccionada a stock.',
      needsFile: false
    },
    assign: {
      label: 'Asignar',
      title: 'Asigna un equipo a un usuario',
      description: 'Completa los datos para asignar el equipo.',
      needsFile: false
    },
    return: {
      label: 'Devolver a proveedor',
      title: 'Marca el equipo como devuelto',
      description: 'Marca la placa seleccionada como devuelta.',
      needsFile: false
    }
  };

  readonly queryStatusOptions: Array<{ label: string; value: string }> = [
    { label: 'Activo', value: 'activo' },
    { label: 'Historico', value: 'historico' },
    { label: 'Disponible', value: 'disponible' },
    { label: 'Devuelto', value: 'devuelto' }
  ];

  readonly inventoryAreaOptions: string[] = ['Tecnologia', 'Operaciones', 'Comercial', 'Riesgos'];

  selectedAction: InventoryAction = 'query';
  file: File | null = null;
  fileName = '';
  isDragging = false;
  isUploading = false;
  showAlertError = false;
  errorMessage = '';
  uploadResult: any = null;
  uploadedAt: Date | null = null;
  responseAlerts: InventoryResponseAlert[] = [];
  revisionAlerts: InventoryRevisionAlert[] = [];
  localRevisionPlates: string[] = [];
  inventorySnapshotRows: Array<Record<string, any>> = [];
  resultMessage = '';
  resultCount = 0;
  resultRows: Array<Record<string, any>> = [];
  resultColumns: string[] = [];
  pendingLoadConfirmation = false;
  loadUnlocked = false;
  confirmLoad = true;
  metricsFilters: InventoryMetricsFilters = {};
  queryFilters: InventoryQueryFilters = {};
  returnToStockPlaca = '';
  returnPlaca = '';
  updateSearchPlaca = '';
  updateRecord: Record<string, any> | null = null;
  updatePayload: InventoryUpdatePayload = {
    placa_inventario: '',
    celular: '',
    direccion: '',
    observaciones: '',
    area: '',
    nombre_equipo: '',
    placa_inventario_cargador: '',
    placa_seguridad_cargador: '',
    valor_comercial: '',
    valor_poliza: ''
  };
  assignPayload: InventoryAssignPayload = {
    placa_inventario: '',
    usuario: '',
    celular: '',
    direccion: '',
    observaciones: '',
    area: '',
    nombre_equipo: '',
    hardware: '',
    placa_inventario_cargador: '',
    placa_seguridad_cargador: '',
    sistema_operativo: '',
    valor_comercial: '',
    valor_poliza: ''
  };
  resultStatusFilter = '';

  constructor(
    private readonly loadingService: LoadingService,
    private readonly inventarioService: InventarioService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadInventorySnapshot();
  }

  get currentActionMeta(): ActionMeta {
    return this.actionMeta[this.selectedAction];
  }

  get requiresFile(): boolean {
    return this.currentActionMeta.needsFile;
  }

  get pageSubtitle(): string {
    return this.currentActionMeta.description;
  }

  get actionButtonLabel(): string {
    if (this.selectedAction === 'query') {
      return 'Consultar';
    }

    if (this.selectedAction === 'load') {
      return 'Load';
    }

    return 'Ejecutar';
  }

  get hasResultRows(): boolean {
    return this.resultRows.length > 0;
  }

  get uniqueStatuses(): string[] {
    const statuses = new Set<string>();
    this.resultRows.forEach((row) => {
      const status = row['estado'] || row['estatus'];
      if (status) {
        statuses.add(String(status));
      }
    });
    return Array.from(statuses).sort();
  }

  getCountByStatus(status: string): number {
    return this.resultRows.filter((row) => {
      const rowStatus = row['estado'] || row['estatus'];
      return String(rowStatus) === status;
    }).length;
  }

  get filteredResultRows(): Array<Record<string, any>> {
    if (!this.resultStatusFilter) {
      return this.resultRows;
    }
    return this.resultRows.filter((row) => {
      const status = row['estado'] || row['estatus'];
      return String(status) === this.resultStatusFilter;
    });
  }

  get visibleActionOptions(): Array<{ value: InventoryAction; label: string }> {
    return this.actionOptions.filter((action) => action.value !== 'load' || this.loadUnlocked);
  }

  selectAction(action: InventoryAction): void {
    if (action === 'load' && !this.loadUnlocked) {
      this.errorMessage = 'Primero ejecuta Cargar archivo y confirma si deseas añadir los registros nuevos.';
      this.showAlertError = true;
      setTimeout(() => (this.showAlertError = false), 5000);
      return;
    }

    this.selectedAction = action;
    this.clearUpdateState();
    if (action !== 'metrics') {
      this.pendingLoadConfirmation = false;
    }
    this.clearResultState();
    if (!this.requiresFile) {
      this.clearFile();
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      this.setFile(input.files[0]);
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;

    const files = event.dataTransfer?.files;
    if (files?.length) {
      this.setFile(files[0]);
    }
  }

  triggerFileInput(): void {
    this.fileInput?.nativeElement.click();
  }

  clearFile(): void {
    this.file = null;
    this.fileName = '';

    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }

  acceptNewRecords(): void {
    this.loadUnlocked = true;
    this.pendingLoadConfirmation = false;
    this.selectedAction = 'load';
    this.showAlertError = false;
    this.errorMessage = '';
    if (this.file) {
      this.upload();
    }
  }

  rejectNewRecords(): void {
    this.resetToStart();
  }

  resetToStart(): void {
    this.selectedAction = 'metrics';
    this.loadUnlocked = false;
    this.pendingLoadConfirmation = false;
    this.metricsFilters = {};
    this.queryFilters = {};
    this.returnToStockPlaca = '';
    this.returnPlaca = '';
    this.clearUpdateState();
    this.assignPayload = {
      placa_inventario: '',
      usuario: '',
      celular: '',
      direccion: '',
      observaciones: '',
      area: '',
      hardware: '',
      placa_inventario_cargador: '',
      placa_seguridad_cargador: '',
      sistema_operativo: '',
      valor_comercial: '',
      valor_poliza: ''
    };
    this.clearResultState();
    this.clearFile();
  }

  upload(): void {
    if (this.requiresFile && !this.file) {
      this.errorMessage = `Selecciona un archivo CSV para ${this.selectedAction}.`;
      this.showAlertError = true;
      setTimeout(() => (this.showAlertError = false), 5000);
      return;
    }

    this.showAlertError = false;
    this.errorMessage = '';
    this.isUploading = true;
    this.loadingService.setLoading(true);

    const request$ = this.buildRequest();
    if (!request$) {
      this.isUploading = false;
      this.loadingService.setLoading(false);
      return;
    }

    request$
      .pipe(finalize(() => {
        this.isUploading = false;
        this.loadingService.setLoading(false);
      }))
      .subscribe({
        next: (response) => {
          this.uploadResult = response;
          this.uploadedAt = new Date();
          this.responseAlerts = Array.isArray(response?.validation_alerts)
            ? response.validation_alerts.map((alert: { tipo?: string; mensaje?: string; placa_inventario?: string }) => ({
                tipo: alert?.tipo || 'info',
                mensaje: this.sanitizeResultText(alert?.mensaje || ''),
                placa_inventario: alert?.placa_inventario
              }))
            : [];
          this.revisionAlerts = this.parseRevisionAlerts(response);
          this.resultMessage = this.sanitizeResultText(response?.message || response?.detail || response?.status || this.uploadSummary || '');
          this.resultCount = Number(response?.count ?? response?.items?.length ?? 0);
          this.resultRows = this.sortRowsByPkDateDesc(Array.isArray(response?.items) ? response.items : []);
          this.resultColumns = this.collectResultColumns(this.resultRows);

          if (response?.requires_load_confirmation || this.hasNewRecords || this.hasRevisionRecords) {
            this.pendingLoadConfirmation = true;
            this.selectedAction = 'metrics';
          } else if (this.selectedAction === 'metrics') {
            this.pendingLoadConfirmation = false;
            this.resultMessage = this.sanitizeResultText(response?.message || 'Archivo procesado correctamente. Listo para cargar a la base de datos.');
          }

          if (this.selectedAction === 'query') {
            this.responseAlerts = [];
            this.resultMessage = this.sanitizeResultText(response?.message || `Consulta ejecutada correctamente. ${this.resultCount} registros encontrados.`);
            if (!this.resultRows.length) {
              this.resultMessage = this.sanitizeResultText(response?.message || 'No se encontraron registros para los filtros seleccionados.');
            }
          }
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.errorMessage = error?.error?.error?.message || error?.error?.message || error?.message || `No fue posible procesar ${this.selectedAction}.`;
          this.showAlertError = true;
          this.cdr.markForCheck();
          setTimeout(() => (this.showAlertError = false), 6000);
        }
      });
  }

  get uploadSummary(): string {
    if (!this.uploadResult) {
      return '';
    }

    if (typeof this.uploadResult === 'string') {
      return this.sanitizeResultText(this.uploadResult);
    }

    return this.sanitizeResultText(this.uploadResult.message || this.uploadResult.detail || this.uploadResult.status || 'Archivo procesado correctamente.');
  }

  get canShowRawResponse(): boolean {
    return !!this.uploadResult && typeof this.uploadResult !== 'string';
  }

  get responseSummaryEntries(): Array<{ label: string; value: string }> {
    if (!this.uploadResult || typeof this.uploadResult === 'string') {
      return this.uploadResult ? [{ label: 'Respuesta', value: this.uploadResult }] : [];
    }

    const preferredFields: Array<[string, string]> = [
      ['Mensaje', 'message'],
      ['Detalle', 'detail'],
      ['Estado', 'status'],
      ['Código', 'code'],
      ['Cantidad', 'count'],
      ['Placas nuevas', 'placas_nuevas_propuestas'],
      ['Placas a revisar', 'placas_requieren_revision'],
      ['Alertas', 'validation_alerts']
    ];

    const usedKeys = new Set<string>();
    const entries: Array<{ label: string; value: string }> = [];

    preferredFields.forEach(([label, key]) => {
      if (this.uploadResult[key] !== undefined && this.uploadResult[key] !== null && this.uploadResult[key] !== '') {
        entries.push({ label, value: this.formatResponseValue(this.uploadResult[key]) });
        usedKeys.add(key);
      }
    });

    const hiddenKeys = new Set(['path', 'route', 'endpoint', 'url']);

    Object.keys(this.uploadResult).forEach((key) => {
      if (usedKeys.has(key)) {
        return;
      }

      if (hiddenKeys.has(key.toLowerCase())) {
        return;
      }

      const value = this.uploadResult[key];
      if (value === undefined || value === null || value === '') {
        return;
      }

      if (Array.isArray(value) && value.length === 0) {
        return;
      }

      if (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0) {
        return;
      }

      entries.push({ label: this.toDisplayLabel(key), value: this.formatResponseValue(value) });
    });

    return entries;
  }

  private setFile(file: File): void {
    if (!file.name.toLowerCase().endsWith('.csv')) {
      this.errorMessage = 'El archivo debe ser CSV.';
      this.showAlertError = true;
      setTimeout(() => (this.showAlertError = false), 5000);
      if (this.fileInput) {
        this.fileInput.nativeElement.value = '';
      }
      return;
    }

    this.file = file;
    this.fileName = file.name;
    this.clearResultState();
    this.computeLocalRevisionPlatesFromFile(file);

    if (this.selectedAction === 'metrics') {
      setTimeout(() => this.upload(), 0);
    }
  }

  private buildRequest() {
    switch (this.selectedAction) {
      case 'metrics':
        return this.file ? this.inventarioService.uploadMetrics(this.file, this.metricsFilters) : null;
      case 'load':
        return this.file ? this.inventarioService.loadInventory(this.file, this.confirmLoad) : null;
      case 'query':
        return this.inventarioService.queryInventory({
          estatus: this.queryFilters.estatus ?? null,
          placa: this.queryFilters.placa ?? null,
          usuario: this.queryFilters.usuario ?? null
        });
      case 'return-to-stock':
        if (!this.returnToStockPlaca.trim()) {
          this.errorMessage = 'Ingresa la placa a devolver a stock.';
          this.showAlertError = true;
          setTimeout(() => (this.showAlertError = false), 5000);
          return null;
        }
        // Validar que el estado sea "Activo"
        return this.inventarioService.queryInventory({
          placa: this.returnToStockPlaca.trim(),
          estatus: null,
          usuario: null
        }).pipe(
          switchMap((queryResult: any) => {
            const items = Array.isArray(queryResult?.items) ? queryResult.items : [];
            const placaRecord = items.find((item: any) => item.placa_inventario?.toLowerCase() === this.returnToStockPlaca.trim().toLowerCase());

            if (!placaRecord || !this.hasStatus(placaRecord, 'activo')) {
              this.errorMessage = 'No se encuentra Placa Activa';
              this.showAlertError = true;
              setTimeout(() => (this.showAlertError = false), 5000);
              return EMPTY;
            }

            if (!this.confirmAction('¿Estás seguro de regresar esta placa a stock?')) {
              return EMPTY;
            }

            return this.inventarioService.returnToStock(this.returnToStockPlaca.trim());
          })
        );
      case 'assign':
        if (!this.assignPayload.placa_inventario.trim() || !this.assignPayload.usuario.trim() || !this.assignPayload.celular.trim() || !this.assignPayload.direccion.trim() || !this.assignPayload.area?.trim()) {
          this.errorMessage = 'Completa placa, usuario, celular, dirección y área para asignar.';
          this.showAlertError = true;
          setTimeout(() => (this.showAlertError = false), 5000);
          return null;
        }
        // Validar que el estado sea "Disponible"
        return this.inventarioService.queryInventory({
          placa: this.assignPayload.placa_inventario.trim(),
          estatus: null,
          usuario: null
        }).pipe(
          switchMap((queryResult: any) => {
            const items = Array.isArray(queryResult?.items) ? queryResult.items : [];
            const placaRecord = items.find((item: any) => item.placa_inventario?.toLowerCase() === this.assignPayload.placa_inventario.trim().toLowerCase());

            if (!placaRecord || !this.hasStatus(placaRecord, 'disponible')) {
              this.errorMessage = 'La placa debe tener estado Disponible para ser asignada.';
              this.showAlertError = true;
              setTimeout(() => (this.showAlertError = false), 5000);
              return EMPTY;
            }

            if (!this.confirmAction('¿Estás seguro de asignar esta placa?')) {
              return EMPTY;
            }

            return this.inventarioService.assignEquipment({
              placa_inventario: this.assignPayload.placa_inventario.trim(),
              usuario: this.assignPayload.usuario.trim(),
              celular: this.assignPayload.celular.trim(),
              direccion: this.assignPayload.direccion.trim(),
              observaciones: this.assignPayload.observaciones?.trim(),
              area: this.assignPayload.area?.trim(),
              hardware: this.assignPayload.hardware?.trim(),
              placa_inventario_cargador: this.assignPayload.placa_inventario_cargador?.trim(),
              placa_seguridad_cargador: this.assignPayload.placa_seguridad_cargador?.trim(),
              sistema_operativo: this.assignPayload.sistema_operativo?.trim(),
              valor_comercial: this.assignPayload.valor_comercial?.trim(),
              valor_poliza: this.assignPayload.valor_poliza?.trim()
            });
          })
        );
      case 'return':
        if (!this.returnPlaca.trim()) {
          this.errorMessage = 'Ingresa la placa a devolver.';
          this.showAlertError = true;
          setTimeout(() => (this.showAlertError = false), 5000);
          return null;
        }
        if (!this.confirmAction('¿Estás seguro de devolver esta placa a proveedor?')) {
          return EMPTY;
        }
        return this.inventarioService.returnEquipment(this.returnPlaca.trim());
      case 'update':
        return null;
    }
    return null;
  }

  searchUpdateRecord(): void {
    const placa = this.updateSearchPlaca.trim();
    if (!placa) {
      this.errorMessage = 'Ingresa la placa a buscar para actualizar.';
      this.showAlertError = true;
      setTimeout(() => (this.showAlertError = false), 5000);
      return;
    }

    this.showAlertError = false;
    this.errorMessage = '';
    this.isUploading = true;
    this.loadingService.setLoading(true);

    this.inventarioService.queryInventory({ placa, estatus: null, usuario: null })
      .pipe(finalize(() => {
        this.isUploading = false;
        this.loadingService.setLoading(false);
      }))
      .subscribe({
        next: (response) => {
          const rows = this.sortRowsByPkDateDesc(Array.isArray(response?.items) ? response.items : []);
          const record = rows.find((item) => String(item['placa_inventario'] || item['placa'] || '').toLowerCase() === placa.toLowerCase()) || rows[0] || null;

          if (!record) {
            this.updateRecord = null;
            this.clearUpdatePayload();
            this.resultRows = [];
            this.resultColumns = [];
            this.uploadResult = response;
            this.resultMessage = 'No se encontró una placa para modificar.';
            this.cdr.markForCheck();
            return;
          }

          this.updateRecord = record;
          this.updatePayload = this.mapUpdatePayload(record);
          this.uploadResult = response;
          this.resultRows = rows;
          this.resultColumns = this.collectResultColumns(rows);
          this.resultMessage = 'Registro encontrado. Ajusta los campos permitidos y guarda los cambios.';
          this.resultCount = rows.length;
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.errorMessage = error?.error?.error?.message || error?.error?.message || error?.message || 'No fue posible buscar la placa a modificar.';
          this.showAlertError = true;
          this.cdr.markForCheck();
          setTimeout(() => (this.showAlertError = false), 6000);
        }
      });
  }

  saveUpdateRecord(): void {
    const payload = this.buildUpdatePayload();
    if (!payload) {
      return;
    }

    if (!this.confirmAction('¿Estás seguro de actualizar estos campos?')) {
      return;
    }

    this.showAlertError = false;
    this.errorMessage = '';
    this.isUploading = true;
    this.loadingService.setLoading(true);

    this.inventarioService.updateInventoryFields(payload)
      .pipe(finalize(() => {
        this.isUploading = false;
        this.loadingService.setLoading(false);
      }))
      .subscribe({
        next: (response) => {
          this.uploadResult = response;
          const updatedItem = response?.item || response?.updated_item || response?.updated || response?.data || null;
          if (updatedItem) {
            this.updateRecord = updatedItem;
            this.updatePayload = this.mapUpdatePayload(updatedItem);
          }
          this.resultMessage = this.sanitizeResultText(response?.message || 'Campos actualizados correctamente.');
          this.resultRows = updatedItem ? [updatedItem] : this.resultRows;
          this.resultColumns = this.collectResultColumns(this.resultRows);
          this.resultCount = Number(response?.count ?? this.resultRows.length ?? 1);
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.errorMessage = error?.error?.error?.message || error?.error?.message || error?.message || 'No fue posible actualizar la placa.';
          this.showAlertError = true;
          this.cdr.markForCheck();
          setTimeout(() => (this.showAlertError = false), 6000);
        }
      });
  }

  private clearResultState(): void {
    this.uploadResult = null;
    this.uploadedAt = null;
    this.showAlertError = false;
    this.errorMessage = '';
    this.responseAlerts = [];
    this.revisionAlerts = [];
    this.localRevisionPlates = [];
    this.resultMessage = '';
    this.resultCount = 0;
    this.resultRows = [];
    this.resultColumns = [];
    this.resultStatusFilter = '';
  }

  clearUpdateState(): void {
    this.updateSearchPlaca = '';
    this.updateRecord = null;
    this.clearUpdatePayload();
  }

  private clearUpdatePayload(): void {
    this.updatePayload = {
      placa_inventario: '',
      celular: '',
      direccion: '',
      observaciones: '',
      area: '',
      nombre_equipo: '',
      placa_inventario_cargador: '',
      placa_seguridad_cargador: '',
      valor_comercial: '',
      valor_poliza: ''
    };
  }

  private buildUpdatePayload(): InventoryUpdatePayload | null {
    if (!this.updateSearchPlaca.trim()) {
      this.errorMessage = 'Ingresa la placa a modificar.';
      this.showAlertError = true;
      setTimeout(() => (this.showAlertError = false), 5000);
      return null;
    }

    return {
      placa_inventario: this.updatePayload.placa_inventario.trim() || this.updateSearchPlaca.trim(),
      celular: this.updatePayload.celular?.trim(),
      direccion: this.updatePayload.direccion?.trim(),
      observaciones: this.updatePayload.observaciones?.trim(),
      area: this.updatePayload.area?.trim(),
      nombre_equipo: this.updatePayload.nombre_equipo?.trim(),
      placa_inventario_cargador: this.updatePayload.placa_inventario_cargador?.trim(),
      placa_seguridad_cargador: this.updatePayload.placa_seguridad_cargador?.trim(),
      valor_comercial: this.updatePayload.valor_comercial?.trim(),
      valor_poliza: this.updatePayload.valor_poliza?.trim()
    };
  }

  private mapUpdatePayload(record: Record<string, any>): InventoryUpdatePayload {
    return {
      placa_inventario: String(record['placa_inventario'] || record['placa'] || this.updateSearchPlaca || '').trim(),
      celular: String(record['celular'] || ''),
      direccion: String(record['direccion'] || ''),
      observaciones: String(record['observaciones'] || ''),
      area: String(record['area'] || ''),
      nombre_equipo: String(record['nombre_equipo'] || ''),
      placa_inventario_cargador: String(record['placa_inventario_cargador'] || ''),
      placa_seguridad_cargador: String(record['placa_seguridad_cargador'] || ''),
      valor_comercial: String(record['valor_comercial'] || ''),
      valor_poliza: String(record['valor_poliza'] || '')
    };
  }

  private confirmAction(message: string): boolean {
    return window.confirm(message);
  }

  private hasStatus(record: Record<string, any>, expectedStatus: string): boolean {
    const status = String(record['estatus'] || record['estado'] || '').toLowerCase();
    return status === expectedStatus.toLowerCase();
  }

  get hasNewRecords(): boolean {
    return (this.uploadResult?.placas_nuevas_propuestas?.length ?? 0) > 0;
  }

  get hasRevisionRecords(): boolean {
    return this.effectiveRevisionPlates.length > 0;
  }

  get effectiveRevisionPlates(): string[] {
    if (this.revisionAlerts.length) {
      return this.revisionAlerts.map((alert) => alert.placa_inventario).filter((plate) => !!plate);
    }

    const serverPlates = this.normalizePlateList(this.uploadResult?.placas_requieren_revision);
    if (serverPlates.length) {
      return serverPlates;
    }

    return this.localRevisionPlates;
  }

  get loadActionEnabled(): boolean {
    return this.loadUnlocked;
  }

  get updateAreaOptions(): string[] {
    const currentArea = this.updatePayload.area?.trim();
    if (!currentArea || this.inventoryAreaOptions.includes(currentArea)) {
      return this.inventoryAreaOptions;
    }

    return [currentArea, ...this.inventoryAreaOptions];
  }

  private parseRevisionAlerts(response: any): InventoryRevisionAlert[] {
    const directAlerts = Array.isArray(response?.revision_alerts)
      ? response.revision_alerts
          .map((alert: { placa_inventario?: string; mensaje?: string }) => ({
            placa_inventario: String(alert?.placa_inventario || '').trim(),
            mensaje: this.sanitizeResultText(alert?.mensaje || response?.revision_message || 'Estas placas de inventario requieren revision, el proveedor ya no las tiene asociadas a facturacion')
          }))
          .filter((alert: InventoryRevisionAlert) => !!alert.placa_inventario)
      : [];

    if (directAlerts.length) {
      return directAlerts;
    }

    const rawPlates = response?.placas_requieren_revision ?? response?.placas_revision_propuestas ?? response?.revision_plates;
    const plates = this.normalizePlateList(rawPlates);
    const message = this.sanitizeResultText(response?.revision_message || 'Estas placas de inventario requieren revision, el proveedor ya no las tiene asociadas a facturacion');

    return plates.map((plate) => ({
      placa_inventario: plate,
      mensaje: message
    }));
  }

  private normalizePlateList(value: unknown): string[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value
      .map((item) => {
        if (typeof item === 'string') {
          return item.trim();
        }

        if (item && typeof item === 'object') {
          const record = item as Record<string, unknown>;
          return String(record['placa_inventario'] || record['placa'] || record['PK'] || '').trim();
        }

        return '';
      })
      .filter((plate) => !!plate);
  }

  private async computeLocalRevisionPlatesFromFile(file: File): Promise<void> {
    try {
      const csvText = await file.text();
      const csvPlates = this.extractCsvInventoryPlates(csvText);
      const csvPlateSet = new Set(csvPlates.map((plate) => plate.toLowerCase()));

      const snapshotRows = this.inventorySnapshotRows.length ? this.inventorySnapshotRows : this.resultRows;
      const dynamoPlates = this.extractDynamoActiveAvailablePlates(snapshotRows);

      this.localRevisionPlates = dynamoPlates.filter((plate) => !csvPlateSet.has(plate.toLowerCase()));
      this.cdr.markForCheck();
    } catch {
      this.localRevisionPlates = [];
    }
  }

  private extractCsvInventoryPlates(csvText: string): string[] {
    const lines = csvText.split(/\r?\n/).filter((line) => line.trim());
    if (!lines.length) {
      return [];
    }

    const delimiter = this.detectCsvDelimiter(lines[0]);
    const headers = this.parseCsvLine(lines[0], delimiter).map((header) => this.normalizeCsvHeader(header));
    const targetIndex = headers.findIndex((header) => header === 'placainventario');

    if (targetIndex < 0) {
      return [];
    }

    const seen = new Set<string>();
    const plates: string[] = [];

    for (let index = 1; index < lines.length; index += 1) {
      const columns = this.parseCsvLine(lines[index], delimiter);
      const plate = String(columns[targetIndex] || '').trim();
      if (!plate) {
        continue;
      }

      const normalizedPlate = plate.toLowerCase();
      if (seen.has(normalizedPlate)) {
        continue;
      }

      seen.add(normalizedPlate);
      plates.push(plate);
    }

    return plates;
  }

  private extractDynamoActiveAvailablePlates(rows: Array<Record<string, any>>): string[] {
    const seen = new Set<string>();
    const plates: string[] = [];

    rows.forEach((row) => {
      const status = String(row['estatus'] || row['estado'] || '').trim().toLowerCase();
      if (status !== 'activo' && status !== 'disponible') {
        return;
      }

      const plate = String(row['placa_inventario'] || row['placa'] || '').trim();
      if (!plate) {
        return;
      }

      const normalizedPlate = plate.toLowerCase();
      if (seen.has(normalizedPlate)) {
        return;
      }

      seen.add(normalizedPlate);
      plates.push(plate);
    });

    return plates;
  }

  private detectCsvDelimiter(headerLine: string): string {
    const commaCount = (headerLine.match(/,/g) || []).length;
    const semicolonCount = (headerLine.match(/;/g) || []).length;
    return semicolonCount > commaCount ? ';' : ',';
  }

  private parseCsvLine(line: string, delimiter: string): string[] {
    const columns: string[] = [];
    let current = '';
    let insideQuotes = false;

    for (let index = 0; index < line.length; index += 1) {
      const character = line[index];

      if (character === '"') {
        if (insideQuotes && line[index + 1] === '"') {
          current += '"';
          index += 1;
        } else {
          insideQuotes = !insideQuotes;
        }
        continue;
      }

      if (character === delimiter && !insideQuotes) {
        columns.push(current);
        current = '';
        continue;
      }

      current += character;
    }

    columns.push(current);
    return columns;
  }

  private normalizeCsvHeader(header: string): string {
    return header
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');
  }

  private loadInventorySnapshot(): void {
    this.isUploading = true;
    this.loadingService.setLoading(true);

    this.inventarioService.queryInventory({})
      .pipe(finalize(() => {
        this.isUploading = false;
        this.loadingService.setLoading(false);
      }))
      .subscribe({
        next: (response) => {
          this.uploadResult = response;
          this.uploadedAt = new Date();
          this.selectedAction = 'query';
          this.resultMessage = response?.message || `Consulta inicial ejecutada correctamente. ${Number(response?.count ?? response?.items?.length ?? 0)} registros encontrados.`;
          this.resultCount = Number(response?.count ?? response?.items?.length ?? 0);
          this.resultRows = this.sortRowsByPkDateDesc(Array.isArray(response?.items) ? response.items : []);
          this.inventorySnapshotRows = this.resultRows;
          this.resultColumns = this.collectResultColumns(this.resultRows);
          this.responseAlerts = [];
          this.revisionAlerts = [];
        },
        error: () => {
          this.resultMessage = 'No fue posible cargar la tabla inicial de DynamoDB.';
          this.resultRows = [];
          this.resultColumns = [];
        }
      });
  }

  private collectResultColumns(rows: Array<Record<string, any>>): string[] {
    const preferredOrder = ['placa_inventario', 'estatus', 'estado', 'usuario', 'placa', 'tipo', 'categoria_activo', 'clase_activo', 'marca', 'serial', 'hardware', 'modelo', 'sistema_operativo', 'valor_poliza', 'valor_comercial', 'marca', 'area', 'observaciones', 'fecha_ingreso', 'fecha_entrega_proveedor', 'fecha_devolucion', 'fecha_retorno', 'direccion', 'celular', 'nombre_equipo', 'placa_seguridad', 'serial_cargador', 'placa_seguridad_cargador', 'placa_inventario_cargador'];
    const discovered = new Set<string>();

    rows.forEach((row) => Object.keys(row || {}).forEach((key) => discovered.add(key)));

    const ordered = preferredOrder.filter((key) => discovered.has(key));
    const rest = Array.from(discovered).filter((key) => !ordered.includes(key));

    return [...ordered, ...rest];
  }

  private sortRowsByPkDateDesc(rows: Array<Record<string, any>>): Array<Record<string, any>> {
    return [...rows].sort((a, b) => this.getPkTimestamp(b?.['PK']) - this.getPkTimestamp(a?.['PK']));
  }

  private getPkTimestamp(pk: unknown): number {
    if (typeof pk !== 'string') {
      return 0;
    }

    const parts = pk.split('#');
    if (parts.length < 2) {
      return 0;
    }

    const parsed = Date.parse(parts[1]);
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  private formatResponseValue(value: unknown): string {
    if (value === null || value === undefined) {
      return '-';
    }

    if (Array.isArray(value)) {
      if (!value.length) {
        return '0 elementos';
      }

      const sample = value.slice(0, 3).map((item) => this.formatResponseValue(item)).join(', ');
      return value.length > 3 ? `${value.length} elementos: ${sample}...` : `${value.length} elementos: ${sample}`;
    }

    if (typeof value === 'object') {
      const keys = Object.keys(value as Record<string, unknown>);
      return keys.length ? `Objeto con ${keys.length} campos` : 'Objeto vacío';
    }

    return this.sanitizeResultText(String(value));
  }

  private sanitizeResultText(text: string): string {
    if (!text) {
      return '';
    }

    return text
      .replace(/\s*Ruta objetivo:\s*\/api\/v1\/Inventory\/[\w-]+\.?/gi, '')
      .replace(/\s{2,}/g, ' ')
      .trim();
  }

  private toDisplayLabel(key: string): string {
    return key
      .replace(/_/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/\b\w/g, (match) => match.toUpperCase());
  }

  getColumnLabel(key: string): string {
    const labelMap: Record<string, string> = {
      fecha_ingreso: 'Fecha asignación',
      fecha_entrega_proveedor: 'Fecha devolución a Proveedor',
      fecha_creacion: 'Fecha creación activo',
      fecha_retorno: 'Fecha Retorno',
      fecha_devolucion: 'Fecha devolución',
      placa_inventario: 'Placa Inventario',
      observaciones: 'Observaciones',
      area: 'Área',
      nombre_equipo: 'Nombre de equipo',
      placa_inventario_cargador: 'Placa Inventario Cargador',
      placa_seguridad_cargador: 'Placa Seguridad Cargador',
      sistema_operativo: 'Sistema Operativo',
      valor_comercial: 'Valor Comercial',
      valor_poliza: 'Valor Póliza'
    };

    return labelMap[key] || this.toDisplayLabel(key);
  }
}